import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFilters } from '../../context/FilterContext';
import { useResponsive } from '../../hooks/useResponsive';
import { DAY_NAMES } from '../../types/filters';
import './DayOfWeekFilter.css';

interface DayOfWeekFilterProps {
  disabled?: boolean;
}

const DayOfWeekFilter: React.FC<DayOfWeekFilterProps> = ({ disabled = false }) => {
  const { t } = useTranslation();
  const { tempFilters, setDayOfWeekFilter, applyTempFilters, cancelTempFilters, hasUnappliedChanges } = useFilters();
  const { isLaptop, isiPad, isiPhone } = useResponsive();
  const [showPicker, setShowPicker] = useState(false);

  const dayOfWeek = tempFilters.dayOfWeek;
  const dateRange = tempFilters.dateRange;

  
  const getAvailableDays = (): number[] => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    const availableDays = new Set<number>();
    
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      availableDays.add(currentDate.getDay());
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return Array.from(availableDays).sort((a, b) => a - b);
  };

  const availableDays = getAvailableDays(); 

  const handleDayToggle = (dayIndex: number) => {
    if (disabled) return;
    
    const newSelectedDays = dayOfWeek.selectedDays.includes(dayIndex)
      ? dayOfWeek.selectedDays.filter(day => day !== dayIndex)
      : [...dayOfWeek.selectedDays, dayIndex];

    setDayOfWeekFilter({
      selectedDays: newSelectedDays,
      allDays: newSelectedDays.length === 7
    });
  };

  const handleAllDaysToggle = () => {
    if (disabled) return;
    
    if (dayOfWeek.allDays) {
      setDayOfWeekFilter({
        selectedDays: [],
        allDays: false
      });
    } else {
      setDayOfWeekFilter({
        selectedDays: [0, 1, 2, 3, 4, 5, 6],
        allDays: true
      });
    }
  };

  const handleWeekdaysOnly = () => {
    if (disabled) return;
    
    setDayOfWeekFilter({
      selectedDays: [1, 2, 3, 4, 5], 
      allDays: false
    });
  };

  const handleWeekendsOnly = () => {
    if (disabled) return;
    
    setDayOfWeekFilter({
      selectedDays: [0, 6], 
      allDays: false
    });
  };

  const getDayName = (dayIndex: number) => {
    const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return t(`dayOfWeekFilter.dayNames.${dayKeys[dayIndex]}`);
  };

  const getButtonText = () => {
    if (dayOfWeek.allDays) {
      return t('dayOfWeekFilter.buttonText.allDays');
    } else if (dayOfWeek.selectedDays.length === 0) {
      return t('dayOfWeekFilter.buttonText.selectDays');
    } else {
      const selectedDayNames = dayOfWeek.selectedDays
        .sort((a, b) => a - b)
        .map(day => getDayName(day));
      
      if (selectedDayNames.length <= 3) {
        return selectedDayNames.join(', ');
      } else {
        return t('dayOfWeekFilter.buttonText.selectedWithMore', {
          selected: selectedDayNames.slice(0, 2).join(', '),
          count: selectedDayNames.length - 2
        });
      }
    }
  };

  const dayTextStyles = {
    fontSize: isiPhone ? '0.85rem' : isiPad ? '0.9rem' : isLaptop ? '1.05rem' : '1.2rem',
    fontWeight: 700,
    color: 'white',
    letterSpacing: '0.3px',
    flex: 1,
    textAlign: 'center' as const,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const
  };

  return (
    <div className={`day-of-week-filter ${disabled ? 'disabled' : ''}`}>
      <button 
        className={`filter-button day-filter-button ${dayOfWeek.selectedDays.length > 0 ? 'active' : ''}`}
        onClick={() => !disabled && setShowPicker(!showPicker)}
        disabled={disabled}
      >
        <span className="day-icon">📅</span>
        <span className="day-text" style={dayTextStyles}>{getButtonText()}</span>
        <span className="dropdown-arrow">{showPicker ? '▲' : '▼'}</span>
      </button>

      {showPicker && !disabled && (
        <div className="day-picker-dropdown">
          <div className="quick-select-section">
            <div className="quick-select-title">{t('dayOfWeekFilter.quickSelect')}</div>
            <div className="quick-select-buttons">
              <button 
                className={dayOfWeek.allDays ? 'active' : ''}
                onClick={handleAllDaysToggle}
              >
                {t('dayOfWeekFilter.buttonText.allDays')}
              </button>
              <button onClick={handleWeekdaysOnly}>{t('dayOfWeekFilter.weekdays')}</button>
              <button onClick={handleWeekendsOnly}>{t('dayOfWeekFilter.weekends')}</button>
            </div>
          </div>
          
          <div className="day-selection-section">
            <div className="day-selection-title">{t('dayOfWeekFilter.individualDays')}</div>
            <div className="day-buttons">
              {availableDays.map((dayIndex) => (
                <button
                  key={dayIndex}
                  className={`day-button ${dayOfWeek.selectedDays.includes(dayIndex) ? 'selected' : ''}`}
                  onClick={() => handleDayToggle(dayIndex)}
                >
                  {getDayName(dayIndex)}
                </button>
              ))}
            </div>
          </div>
          
          <div className="picker-actions">
            <button 
              className="cancel-button"
              onClick={() => {
                cancelTempFilters();
                setShowPicker(false);
              }}
              disabled={!hasUnappliedChanges}
            >
              {t('dayOfWeekFilter.cancel')}
            </button>
            <button 
              className="apply-button"
              onClick={() => {
                applyTempFilters();
                setShowPicker(false);
              }}
            >
              {t('dayOfWeekFilter.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayOfWeekFilter;